import { store, store$ } from '@redux/store';
import { selectBrochureDetails, brochureSliceActions } from 'features/leadGenerators';
import {
    filter, map, fromEvent,
    pairwise, distinctUntilKeyChanged,
    distinctUntilChanged, merge,
    withLatestFrom,
    BehaviorSubject
} from 'rxjs';
import { selectUiState, selectModalListingInformation } from 'layout/redux/selectors';
import LoadingStatus from 'enums/loadingStatus';
import modalService from 'layout/services/modalService';
import HousingProductType from "constants/housingProductType";
import toBoolean from 'utils/toBoolean';
import fetchBrochureBlob from '../apis/fetchBrochureBlob';


const getState = () => store.getState();
const isComingSoonListing = (state) => selectModalListingInformation(state)?.isComingSoon ?? false;

// Observable for clicks on the document body
const bodyClick$ = fromEvent(document.body, 'click');

const brochureBlobHolder = { blob: null };
const brochureDownloadState = { isDownloadReady: LoadingStatus.Idle };
const isOverlayClick$ = new BehaviorSubject(false);

/**
 * Checks if the click event occurred on a specific button.
 * @param {Event} event - The click event object.
 * @param {string} selector - The CSS selector for the target button.
 * @returns {boolean} - Returns true if the click is on the specified button, otherwise false.
 */
const isButtonClick = (event, selector) => !!event.target.closest(selector);

/**
 * Shows the loading indicator and hides the success indicator.
 */
const showLoading = () => {
    const loadingElement = document.querySelector('[data-brochure-download-status]');
    const successElement = document.querySelector('[data-brochure-download-status-completed]');

    if (loadingElement) {loadingElement.hidden = false;}
    if (successElement) {successElement.hidden = true;}
};

/**
 * Hides the loading indicator and shows the success indicator.
 */
const showSuccess = () => {
    const loadingElement = document.querySelector('[data-brochure-download-status]');
    const successElement = document.querySelector('[data-brochure-download-status-completed]');

    if (loadingElement) {loadingElement.hidden = true;}
    if (successElement) {successElement.hidden = false;}
};

/**
 * Fetches the PDF file as a Blob from the given URL.
 * @param {string} url - The URL of the PDF file to download.
 * @returns {Promise<Blob|null>} - A promise that resolves with the Blob object or null on failure.
 */
const fetchPdfBlob = async (url) => {
    try {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) {
            return null;
        }
        return response.blob();
    } catch {
        return null;
    }
};

/**
 * Triggers a download of the given Blob using a temporary link.
 * @param {Blob} blob - The Blob object to download.
 * @param {string} fileName - The name of the downloaded file.
 */
const triggerFileDownload = (blob, fileName) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
};

/**
 * Generates a file name based on the listing name or current timestamp.
 * @param {string} listingName - The name of the listing (optional).
 * @returns {string} - A formatted file name for the PDF.
 */
const generateFileName = (listingName) => {
    if (listingName) {
        return `${listingName.replace(/\s+/g, '_')}.pdf`;
    }
    const timestamp = new Date().toISOString().replace(/[:.-]/g, '');
    return `brochure_${timestamp}.pdf`;
};

/**
 * Downloads a PDF brochure by fetching the file and triggering a download,
 * while updating the UI loading and success indicators.
 * @param {string} url - The URL of the PDF file.
 * @param {string} [listingName=''] - Optional name for the listing to create a custom file name.
 */
const downloadPdfBrochure = async (url, listingName = '') => {
    showLoading();

    const blob = await fetchPdfBlob(url);

    if (blob) {
        const fileName = generateFileName(listingName);
        triggerFileDownload(blob, fileName);
    }

    showSuccess();

    setTimeout(() => {
        modalService.clearHtml();
        brochureDownloadState.isDownloadReady = LoadingStatus.Idle;
    }, 3000);
};

const downloadBrochurePdfFromBlob = (blob, fileName) => {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Release memory
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

    const loadingElement = document.querySelector('[data-brochure-download-status]');
    const successElement = document.querySelector('[data-brochure-download-status-completed]');

    if (loadingElement) {
        loadingElement.hidden = true;
    }

    if (successElement) {
        successElement.hidden = false;
    }

    setTimeout(() => {
        modalService.clearHtml();
        brochureDownloadState.isDownloadReady = LoadingStatus.Idle;
    }, 3000);
};

/**
 * Determines the correct brochure type for the API call.
 * @param {Object} modalInformation - The modal information object.
 * @returns {string} - The brochure type string.
 */
const getBrochureType = (modalInformation) => {
    switch (modalInformation?.type) {
        case 'spec':
            return 'housingunit';
        case 'plan':
            return 'houseplan';
        case 'community':
            if (modalInformation?.listingInformation?.isMasterPlanCommunity) {
                return 'masterplannedcommunity';
            }
            return 'community';
        default:
            // Fallback: If the type doesn't match any known case, return the original type value.
            // This ensures that any new or unexpected types are still passed through to the API.
            return modalInformation?.type;
    }
};

/**
 * Determines if brochure download is allowed based on listing status.
 * Returns false if the listing is marked as "coming soon".
 *
 * @returns {boolean} True if download is allowed, false otherwise.
 */
function isDownloadAllowed() {
    const state = getState();
    return !toBoolean(isComingSoonListing(state));
}


/**
 * Dispatches an action to fetch the brochure download URL.
 * @param {Object} modalInformation - Contains details about the brochure.
 * @param {string} modalInformation.type - The type of the brochure.
 * @param {string|number} modalInformation.id - The unique identifier of the brochure.
 */
const dispatchFetchBrochureDownloadUrl = (modalInformation) => {
    if (!isDownloadAllowed()) {
        return;
    }

    const brochureType = getBrochureType(modalInformation);

    store.dispatch(brochureSliceActions.fetchBrochureDownloadUrl({
        parameters: {
            type: "getBrochureDownloadUrl",
            pdfbrochuretype: brochureType,
            id: modalInformation?.id
        }
    }));
};

// Observable to fetch PDF download URL
const onBrochureDownloadClick$ = store$.pipe(
    map(selectUiState),
    distinctUntilKeyChanged('isFetchingModal'),
    filter((uiState) => uiState.isFetchingModal),
    map((uiState) => uiState.modalInformation),
    filter((modalInformation) =>
        modalInformation?.leadGeneratorOpener === 'get-brochure-paid' &&
        modalInformation?.name === 'leadGenerator'
    ),
);

// Observable when user click on skip or get more info in the recommendation screen
const recommendedScreenButtonClicks$ = bodyClick$.pipe(
    filter((event) =>
        isButtonClick(event, '[data-skip-recommendations]') ||
        isButtonClick(event, '[data-get-reco-brochure]')
    )
);

// Observable when user goes from leadGenerator to thankYou screen
const leadGeneratorToThankYouTransition$ = store$.pipe(
    map(selectUiState),
    map((uiState) => uiState.modalInformation),
    distinctUntilChanged(),
    pairwise(),
    filter(([prev, curr]) =>
        (prev?.leadGeneratorOpener === 'get-brochure-paid') &&
        (prev?.name === 'leadGenerator' && curr?.name === 'thankYouScreen')
    )
);

/**
 * Observable that emits when the store contains modal information for:
 * - A recommended screen modal
 * - With a brochure download opener ('get-brochure-paid')
 * 
 * Filters distinct modalInformation changes to avoid duplicate emissions
 * for the same modal state.
 */
const recommendedScreenWithBrochureOpener$ = store$.pipe(
    map(selectUiState),
    map((uiState) => uiState.modalInformation),
    distinctUntilChanged(),
    filter((modalInformation) =>
        modalInformation?.name === 'recommendedScreen' &&
        modalInformation?.leadGeneratorOpener === 'get-brochure-paid'
    )
);

/**
 * Observable that emits when:
 * 1. A click event occurs on the overlay element (data-overlay-element)
 * 2. Combined with the latest state where the modal is:
 *    - A recommended screen
 *    - With brochure download opener
 * This effectively tracks overlay clicks only when shown as part of
 * the brochure download recommended screen flow.
 */
const overlayClickOnRecommendedScreen$ = bodyClick$.pipe(
    filter((event) => isButtonClick(event, '[data-overlay-element]')),
    withLatestFrom(recommendedScreenWithBrochureOpener$)
);

// Extract brochure URL and listing name from store
const extractBrochureDownloadInfo = () => {
    const brochureDetails = selectBrochureDetails(getState());
    const modalInfo = selectUiState(getState()).modalInformation;

    if (!brochureDetails.url || brochureDetails.fetchStatus.toString() !== LoadingStatus.Succeeded.toString()) {
        return null;
    }

    const urlData = JSON.parse(brochureDetails.url);
    const type = modalInfo?.type;

    const { listingInformation = {} } = modalInfo || {};
    const { address = '', name = '', communityName = '' } = listingInformation;

    let listingName = '';

    if (type === HousingProductType.Spec().getName()) {
        const trimmedAddress = address.split('.')[0].trim();
        listingName = name ? `${trimmedAddress} (${name})` : trimmedAddress;
    } else if (type === HousingProductType.Plan().getName()) {
        listingName = `${name} Plan`;
    } else if (type === HousingProductType.Community().getName()) {
        listingName = communityName;
    } else {
        listingName = '';
    }

    return { url: urlData.encryptedPdfBrochureUrl, listingName };
};

// Handles immediate download when triggered by specific user interactions
const onDownloadPdfBrochure$ = merge(
    recommendedScreenButtonClicks$,
    leadGeneratorToThankYouTransition$,
    overlayClickOnRecommendedScreen$
).pipe(
    map(() => extractBrochureDownloadInfo()),
    filter(Boolean)
);

// Emits as soon as the brochure URL is ready in the store
const onBrochureDownloadUrlReady$ = store$.pipe(
    map(selectBrochureDetails),
    distinctUntilChanged(),
    map(() => extractBrochureDownloadInfo()),
    filter(Boolean)
);

onBrochureDownloadClick$.subscribe(dispatchFetchBrochureDownloadUrl);

onBrochureDownloadUrlReady$.subscribe(({ url }) => {
    brochureBlobHolder.blob = null; // Clear old blob immediately
    fetchBrochureBlob(url).subscribe((newBlob) => {
        brochureBlobHolder.blob = newBlob;
    });
});

bodyClick$.subscribe((event) => {
    const isOverlay = isButtonClick(event, '[data-overlay-element]');
    isOverlayClick$.next(isOverlay);
});

const modalName$ = store$.pipe(
    map(selectUiState),
    map((uiState) => uiState.modalInformation?.name),
    filter((name) => !!name),
    distinctUntilChanged()
);

/**
 * Handles the logic for downloading the brochure PDF,
 * either from a previously fetched Blob or by requesting the file via URL.
 * @param {string} url - The brochure download URL
 * @param {string} listingName - The name of the listing for file naming
 */
const handlePdfBrochureDownload = ({ url, listingName }) => {
    brochureDownloadState.isDownloadReady = LoadingStatus.Loading;

    if (brochureBlobHolder.blob) {
        downloadBrochurePdfFromBlob(brochureBlobHolder.blob, listingName);
    } else {
        downloadPdfBrochure(url, listingName);
    }
};

onDownloadPdfBrochure$
    .pipe(
        withLatestFrom(modalName$, isOverlayClick$),
        filter(([, modalName, isOverlayClick]) => {
            const forbiddenModals = ['leadGenerator', 'thankYouScreen', 'leadModal'];
            return !(isOverlayClick && forbiddenModals.includes(modalName));
        }),
        filter(() => brochureDownloadState.isDownloadReady === LoadingStatus.Idle),
        filter(isDownloadAllowed)
    )
    .subscribe(([{ url, listingName }]) => {
        handlePdfBrochureDownload({ url, listingName });
    });